/**
 * relationConstants — single source of truth for subject/family relations.
 *
 * Shared by:
 *  - components/account/AddFamilyMemberModal.jsx — maps a chosen relation to a
 *    child/adult entity_type at write time (the launch-time signal the Step-0
 *    picker uses to keep adults out of the subject list).
 *  - pages/SoknadWizard/SubjectConfirmStep.jsx — the Tier-2 "Hva er {name} for
 *    deg?" relation dropdown, and the adult-relation advance gate.
 *
 * Keeping the adult set in ONE place means the modal's entity_type mapping and
 * the confirm step's adult gate can never drift apart (e.g. adding "besteforelder"
 * as an adult relation updates both consumers at once).
 */

// Relation values that denote a CHILD the søknad could legitimately be about.
// These map to entity_type 'child' on manual add and advance normally in the
// Tier-2 confirm step.
export const CHILD_RELATIONS = ['sønn', 'datter', 'søster', 'bror'];

// Relation values that denote an ADULT. The søknad cannot be filed for an adult
// in the launch scope, so these:
//  - map to entity_type 'adult' on manual add (filtered out of the picker), and
//  - block advancing in the Tier-2 confirm step (with the children-only redirect).
export const ADULT_RELATIONS = ['ektefelle', 'partner', 'mor', 'far', 'forelder'];

// The sentinel "this is me" relation. Selected when the picked subject IS the
// applying user themselves (the soft residual Layer-1 entityId exclusion can't
// catch — e.g. a self-mention mis-bucketed into family_members under a different
// entityId). Selecting it blocks advance, same as an adult relation.
export const SELF_RELATION = 'self';

/**
 * Is this relation value one the user cannot file a hjelpestønad søknad against
 * in the launch scope (an adult, or the user themselves)? Drives the confirm
 * step's advance gate.
 */
export function isBlockingRelation(relation) {
  return relation === SELF_RELATION || ADULT_RELATIONS.includes(relation);
}

/**
 * Relation options for the Tier-2 confirm dropdown ("Hva er {name} for deg?").
 * Order: the valid child relations first, then the blocking ones (adults +
 * "this is me"), then the free-text "annen" fallback. The blocking options ARE
 * offered on purpose — an honest user who picked an adult/themselves needs a
 * truthful answer that then routes them back to the picker, rather than being
 * forced to mis-state the relation.
 */
export const RELATION_OPTIONS = [
  { value: 'søster', label: 'Søster' },
  { value: 'bror', label: 'Bror' },
  { value: 'sønn', label: 'Sønn' },
  { value: 'datter', label: 'Datter' },
  { value: 'mor', label: 'Mor' },
  { value: 'far', label: 'Far' },
  { value: SELF_RELATION, label: 'Dette er meg (forelder/søker)' },
  { value: 'annen', label: 'Annen slektning' },
];
